import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Box, Stack } from "@mui/material"
import fp from "lodash/fp"

import type { Powerline } from "types/powerlines.types"
import type { Span } from "types/spans.types"

import PageTitle from "widgets/common/PageTitle"
import EntityTag from "widgets/common/EntityTag"
import { getSpanDisplayName } from "helpers/utils/spans"
import SpanPagination from "widgets/spans/SpanPagination"
import AssetContextBlock from "widgets/common/AssetContextBlock"
import MetaItem, { MetaList } from "widgets/common/MetaItem"
import { customValueFormatter } from "helpers/formatters/plots/plots"
import useContextDevices from "helpers/hooks/devices/useContextDevices"
import useTower from "helpers/hooks/towers/useTower"

interface SpanSummaryProps {
  span: Span
  powerline?: Powerline
  powerlineLoading?: boolean
  /** The page's controls, such as the date range. */
  actions?: ReactNode
}

const SpanSummary = ({
  span,
  powerline,
  powerlineLoading,
  actions,
}: SpanSummaryProps) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"

  const displayName = getSpanDisplayName(span, powerline?.span_ids, t)

  // The span's two towers load one by one, not with every tower of the org.
  const { tower: firstTower, isLoading: firstTowerLoading } = useTower({
    id: span.tower_ids[0],
  })
  const { tower: secondTower, isLoading: secondTowerLoading } = useTower({
    id: span.tower_ids[1],
  })
  const towersById = fp.keyBy("id", fp.compact([firstTower, secondTower]))
  const { devices, isLoading: devicesLoading } = useContextDevices({
    asset: { entity: "span", id: span.id },
  })

  // The id stands in for a tower with no name.
  const towers = span.tower_ids.map((towerId) => ({
    entity: "tower" as const,
    id: towerId,
    name: towersById[towerId]?.name || String(towerId),
  }))

  return (
    <Box
      sx={{
        marginBottom: "1rem",
      }}
      data-testid="span-summary"
    >
      <Stack
        flexDirection="row"
        flexWrap="wrap"
        alignItems="center"
        columnGap={2}
        rowGap={1}
        marginBottom={1.5}
      >
        <EntityTag entity="span" />
        <PageTitle
          text={displayName}
          styles={{ fontStyle: span.name ? "normal" : "italic" }}
        />
        <SpanPagination spanId={span.id} powerline={powerline} />
        {/* The end of the title row on desktop, a full-width row of its own on mobile. */}
        {actions && (
          <Box sx={{ marginLeft: { md: "auto" }, width: { xs: "100%", md: "auto" } }}>
            {actions}
          </Box>
        )}
      </Stack>
      <Stack gap={1.5}>
        <MetaList>
          <MetaItem label="ID">{span.id}</MetaItem>
          {!fp.isNil(span.length) && (
            <MetaItem label={t("spans.LENGTH")}>
              {customValueFormatter({
                suffix: "m",
                decimalPlaces: 2,
                formatUnits: true,
                locale,
              })(span.length)}
            </MetaItem>
          )}
          {!fp.isNil(span.ampacity_nominal) && (
            <MetaItem label={t("spans.AMPACITY_NOMINAL")}>
              {customValueFormatter({
                suffix: "A",
                decimalPlaces: 0,
                formatUnits: true,
                locale,
              })(span.ampacity_nominal)}
            </MetaItem>
          )}
          {span.phase_id && <MetaItem label={t("spans.PHASE")}>{span.phase_id}</MetaItem>}
        </MetaList>
        <AssetContextBlock
          powerLines={powerline ? [powerline] : []}
          location={{ label: t("spans.TOWERS"), refs: towers }}
          devices={{ items: devices }}
          loading={{
            powerLines: powerlineLoading,
            location: firstTowerLoading || secondTowerLoading,
            devices: devicesLoading,
          }}
        />
      </Stack>
    </Box>
  )
}

export default SpanSummary
